/**
 * Cart Service - Functional approach with shared module state
 * Enhanced cart management with improved error handling and data sharing
 */

import type { CouponREST } from "@/types/woocommerce.types";
import type { WooCoupon } from "@/types/woocommerce.types";
import type { COUPON_ERROR_TYPES } from "@/lib/constants/payment-error-constants";
import { COUPON_ERROR_CODES } from "@/lib/constants/payment-error-constants";

// ===== MODULE-LEVEL STATE =====
// Shared across all imports of this module
let cart_token: string | null = null;

/**
 * Initializes WooCommerce cart session with fail-fast validation
 * Fetches a new cart token from the WooCommerce Store API
 * @returns {Promise<boolean>} True if session initialized successfully
 */
export const initialize_cart_session = async (): Promise<boolean> => {
  // check and get cart_token from sessionStorage
  cart_token = sessionStorage.getItem("cart_token");

  // VALIDATION: Check if cart token already exists
  if (cart_token && cart_token !== "") {
    console.warn("Cart session already initialized");
    return true;
  }

  try {
    const token = get_cart_token();
    const response = await fetch(`/api/woocommerce/cart`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Cart-Token": token || "",
      },
      credentials: "same-origin",
    });

    cart_token = response.headers.get("Cart-Token");

    // VALIDATION: Verify cart_token exists in response
    if (!cart_token) {
      console.error("No cart token received from WooCommerce");
      return false;
    }

    // SET CART TOKEN
    set_cart_token(cart_token);

    return true;
  } catch (error) {
    console.error("Cart Service - Failed to initialize cart session", {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
    return false;
  }
};

/**
 * Clears all items from the current cart session with fail-fast validation
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const clear_cart = async (): Promise<boolean> => {
  // VALIDATION: Check if cart token exists
  if (!cart_token) {
    console.error("Cannot clear cart: No cart session initialized");
    return false;
  }

  try {
    await fetch("/api/woocommerce/cart", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "Cart-Token": cart_token || "",
      },
      credentials: "same-origin",
    });

    return true;
  } catch (error) {
    console.error("Cart Service - Failed to clear cart", {
      cart_token,
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
    return false;
  }
};

/**
 * Adds a product variant to the current cart session
 * @param variant - Product variant to add
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const add_product_to_cart = async ({
  variant_id,
}: {
  variant_id: number;
}): Promise<boolean> => {
  // VALIDATION: Check if cart token exists
  if (!cart_token) {
    console.error("Cannot add product: No cart session initialized");
    return false;
  }

  // VALIDATION: Check required variant fields
  if (!variant_id) {
    console.error("Invalid variant data: missing variant_id");
    return false;
  }

  const cart_item = {
    id: variant_id,
  };

  try {
    const response = await fetch("/api/woocommerce/cart", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Cart-Token": cart_token || "",
      },
      credentials: "same-origin",
      body: JSON.stringify(cart_item),
    });

    if (!response.ok) {
      console.error("Failed to add product to cart:", response);
      console.error("Failed to add product to cart - status:", response.status);
      return false;
    }

    return true;
  } catch (error) {
    console.error("Cart Service - Failed to add product to cart", {
      variant_id,
      cart_token,
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
    return false;
  }
};

/**
 * Gets the current cart token
 * @returns {string | null} Current cart token or null if not initialized
 */
export const get_cart_token = (): string | null => {
  return cart_token;
};

/**
 * Manually sets the cart token (useful for restoring from storage)
 * @param token - Cart token to set
 */
export const set_cart_token = (token: string | null): void => {
  cart_token = token;
  sessionStorage.setItem("cart_token", token || "");
};

/**
 * Checks coupon eligibility against intrinsic properties and cart context
 * @param coupon_data - Raw coupon data from API
 * @param context - Additional context for validation (cart total, existing coupons, etc.)
 * @returns Validation result with error details
 */
const check_coupon_eligibility = (
  coupon_data: WooCoupon,
  context: {
    cart_total?: number;
    existing_coupons?: string[];
    cart_product_id?: number;
    cart_variant_id?: number;
    email?: string;
  } = {}
): {
  is_valid: boolean;
  error_code?: COUPON_ERROR_TYPES;
} => {
  console.log("coupon_data", coupon_data);
  // 1. Check if coupon exists (most fundamental check)
  if (!coupon_data || !coupon_data.id) {
    return {
      is_valid: false,
      error_code: COUPON_ERROR_CODES.COUPON_NOT_FOUND,
    };
  }

  // 2. Check expiration date (time-based validity)
  if (coupon_data.date_expires) {
    const expiry_date = new Date(coupon_data.date_expires);
    const current_date = new Date();

    if (current_date > expiry_date) {
      return {
        is_valid: false,
        error_code: COUPON_ERROR_CODES.COUPON_EXPIRED,
      };
    }
  }

  // 3. Check usage limit (availability check)
  if (
    coupon_data.usage_limit &&
    coupon_data.usage_count >= coupon_data.usage_limit
  ) {
    return {
      is_valid: false,
      error_code: COUPON_ERROR_CODES.COUPON_LIMIT_REACHED,
    };
  }

  // 4. Check individual use restriction (conflicts with other coupons)
  if (
    coupon_data.individual_use &&
    context.existing_coupons &&
    context.existing_coupons.length > 0
  ) {
    return {
      is_valid: false,
      error_code: COUPON_ERROR_CODES.COUPON_INDIVIDUAL_USE,
    };
  }

  // 5. Check product restrictions (product eligibility)
  // 5a. Excluded products (blacklist): if excluded_product_ids present, cart product/variant must NOT be in the list
  if (coupon_data.excluded_product_ids?.length > 0) {
    const cart_ids = [context.cart_product_id, context.cart_variant_id].filter(
      (id) => id !== undefined
    );

    const is_excluded = cart_ids.some((id) =>
      coupon_data.excluded_product_ids.includes(id)
    );

    if (is_excluded) {
      return {
        is_valid: false,
        error_code: COUPON_ERROR_CODES.COUPON_PRODUCT_RESTRICTION,
      };
    }
  }

  // 5b. Allowed products (whitelist):
  // - If product_ids has values, cart product/variant MUST be in the list
  // - If product_ids is empty or undefined, all products are allowed (subject to blacklist above)
  if (coupon_data.product_ids?.length > 0) {
    const cart_ids = [context.cart_product_id, context.cart_variant_id].filter(
      (id) => id !== undefined
    );

    const is_allowed = cart_ids.some((id) =>
      coupon_data.product_ids.includes(id)
    );

    if (!is_allowed) {
      return {
        is_valid: false,
        error_code: COUPON_ERROR_CODES.COUPON_PRODUCT_RESTRICTION,
      };
    }
  }

  // 6. Check minimum amount requirement (cart value validation)
  if (
    coupon_data.minimum_amount &&
    parseFloat(coupon_data.minimum_amount) > 0 &&
    context.cart_total !== undefined
  ) {
    if (context.cart_total < parseFloat(coupon_data.minimum_amount)) {
      return {
        is_valid: false,
        error_code: COUPON_ERROR_CODES.COUPON_MINIMUM_AMOUNT,
      };
    }
  }

  // 7. Check maximum amount limit (cart value validation)
  if (
    coupon_data.maximum_amount &&
    parseFloat(coupon_data.maximum_amount) > 0 &&
    context.cart_total !== undefined
  ) {
    if (context.cart_total > parseFloat(coupon_data.maximum_amount)) {
      return {
        is_valid: false,
        error_code: COUPON_ERROR_CODES.COUPON_MAXIMUM_AMOUNT,
      };
    }
  }

  // 8. Check if coupon has already been used by the provided email
  // WooCommerce's `used_by` can contain user IDs or billing emails depending on usage context.
  // We only perform the email check when an email is provided in the validation context.
  // if (context.email && Array.isArray(coupon_data.used_by)) {
  //   const normalized_email = String(context.email).trim().toLowerCase();
  //   const has_been_used_by_email = coupon_data.used_by.some((entry) =>
  //     String(entry).trim().toLowerCase() === normalized_email,
  //   );

  //   if (has_been_used_by_email) {
  //     return {
  //       is_valid: false,
  //       error_code: "COUPON_ALREADY_USED",
  //     };
  //   }
  // }

  return { is_valid: true };
};

/**
 * Retrieves and validates coupon information by ID or code
 * @param {string | number} code - The coupon ID or code to retrieve
 * @param {object} context - Additional context for validation
 * @returns {Promise<{success: boolean, data?: CouponREST, error?: {code: string, message: string}}>}
 */
export const get_coupon = async (
  code: string | number,
  context: {
    cart_total?: number;
    existing_coupons?: string[];
    cart_product_id?: number;
    email?: string;
  } = {}
): Promise<{
  success: boolean;
  data?: CouponREST;
  error_code?: COUPON_ERROR_TYPES;
}> => {
  if (!code) {
    return {
      success: false,
      error_code: COUPON_ERROR_CODES.COUPON_CODE_REQUIRED,
    };
  }

  try {
    const token = get_cart_token();
    const response = await fetch(
      `/api/woocommerce/coupon?code=${encodeURIComponent(code)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "Cart-Token": token || "",
          Accept: "application/json",
        },
        credentials: "same-origin",
      }
    );

    if (!response.ok) {
      const error_data = await response.json();
      return {
        success: false,
        error_code: error_data.code || COUPON_ERROR_CODES.COUPON_NETWORK_ERROR,
      };
    }

    const coupon_data: WooCoupon = await response.json();

    // Check coupon eligibility with context
    const validation_result = check_coupon_eligibility(coupon_data, context);

    if (!validation_result.is_valid) {
      return {
        success: false,
        error_code:
          validation_result.error_code || COUPON_ERROR_CODES.COUPON_NOT_FOUND,
      };
    }

    // Return only the essential coupon data for frontend use
    const filtered_coupon: CouponREST = {
      id: coupon_data.id,
      code: coupon_data.code,
      amount: coupon_data.amount,
      discount_type: coupon_data.discount_type,
      description: coupon_data.description,
      free_shipping: coupon_data.free_shipping,
    };

    return {
      success: true,
      data: filtered_coupon,
    };
  } catch (error) {
    console.error("Cart Service - Failed to retrieve coupon", {
      code,
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });

    return {
      success: false,
      error_code: COUPON_ERROR_CODES.COUPON_NETWORK_ERROR,
    };
  }
};

/**
 * Applies a coupon to the current cart session
 * @param {string} coupon_code - The coupon code to apply
 * @returns {Promise<boolean>} True if successful, false otherwise
 */
export const apply_coupon = async ({
  coupon_code,
}: {
  coupon_code: string;
}): Promise<boolean> => {
  if (!cart_token) {
    console.error("Cannot apply coupon: No cart session initialized");
    return false;
  }

  if (!coupon_code) {
    console.error("Cannot apply coupon: No coupon code provided");
    return false;
  }

  try {
    await fetch(`/api/woocommerce/coupon`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Cart-Token": cart_token || "",
      },
      credentials: "same-origin",
      body: JSON.stringify({ code: coupon_code }),
    });

    return true;
  } catch (error) {
    console.error("Cart Service - Failed to apply coupon", {
      coupon_code,
      cart_token,
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
    return false;
  }
};
